<script setup>
import { Link } from '@inertiajs/vue3'
import { intlFormatDistance } from 'date-fns'
import { computed } from 'vue'

const props = defineProps({
  evaluation: {
    type: Object,
    required: true,
  },
})

// Address-less evaluations (early drafts, EHR-only rows) must not render blank
const cardTitle = computed(() => {
  const evaluation = props.evaluation
  if (evaluation?.address?.lahiaadress) {
    return evaluation.address.lahiaadress
  }
  if (evaluation.cadaster_id) {
    return evaluation.cadaster_id
  }
  if (evaluation.object_identifier && evaluation.object_identifier !== 'undefined') {
    return `EHR ${evaluation.object_identifier}`
  }
  return `Hindamine #${evaluation.id}`
})

// Cadastre outline as an SVG path in a 0–100 viewBox: bbox-fit, y-flipped
// (EPSG:3301 y grows north, SVG y grows down). Cheap — runs once per card.
const outlinePath = computed(() => {
  const geometry = props.evaluation.parcel_geometry
  const rings =
    geometry?.type === 'Polygon'
      ? geometry.coordinates
      : geometry?.type === 'MultiPolygon'
        ? geometry.coordinates.flat()
        : null
  if (!rings?.length) {
    return null
  }

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  rings.forEach(ring =>
    ring.forEach(([x, y]) => {
      minX = Math.min(minX, x)
      minY = Math.min(minY, y)
      maxX = Math.max(maxX, x)
      maxY = Math.max(maxY, y)
    }),
  )
  const width = maxX - minX
  const height = maxY - minY
  if (!width || !height) {
    return null
  }

  const scale = 88 / Math.max(width, height)
  const offsetX = (100 - width * scale) / 2
  const offsetY = (100 - height * scale) / 2

  return rings
    .map(
      ring =>
        'M' +
        ring
          .map(
            ([x, y]) =>
              `${((x - minX) * scale + offsetX).toFixed(1)},${((maxY - y) * scale + offsetY).toFixed(1)}`,
          )
          .join('L') +
        'Z',
    )
    .join(' ')
})
</script>

<template>
  <Link :href="route('evaluation.show', evaluation.id)" class="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:bg-gray-100 transition-all duration-300 ease-in-out relative overflow-hidden">
      <div class="absolute left-0 top-0 bottom-0 w-1 rounded-l-lg" :class="evaluation.status === 'in_progress' ? 'bg-yellow-300' : 'bg-green-300'"></div>
      <!-- Cadastre outline on the right half, fading toward the text -->
      <div
          v-if="outlinePath"
          class="absolute inset-y-0 right-0 w-1/2 pointer-events-none mask-[linear-gradient(to_left,black_45%,transparent)]"
          :class="evaluation.property_kind === 'land' ? 'text-emerald-500' : 'text-blue-400'"
          data-testid="evaluation-parcel-outline"
      >
          <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" class="h-full w-full opacity-40">
              <path :d="outlinePath" fill="currentColor" fill-opacity="0.12" stroke="currentColor" stroke-width="1.5" vector-effect="non-scaling-stroke" />
          </svg>
      </div>
      <div class="relative flex flex-col justify-between">
          <div class="font-bold text-gray-800">
              {{ cardTitle }}
          </div>
          <div class="text-sm font-semibold tracking-tight text-gray-600">
              {{ evaluation?.address_text }}
          </div>
          <div class="flex items-center">
              <span class="text-gray-500 text-xs tracking-tighter">
                  uuendatud {{ intlFormatDistance(new Date(evaluation.updated_at_ms), new Date(), { addSuffix: true, locale: 'et' }) }}
              </span>
          </div>
      </div>
  </Link>
</template>
